import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import sqftShape from '../../../canvas/core/shapes/sqftShape';
import { iconHeight } from '../../../../assetsSQFT';
import {getCabinetInteriorHeight} from '../../../../helpers/tallCabinetInteriorHelper';
import {getShape3dModelUrl} from "../../../../helpers/sqft3dArHelper";
import {getCabinetSectionsFromModel} from "../../../ar3d/threeD/glbViewer/scene/modelsLoader/cabinetInterior/cabinetSectionsParser";
import {updateTallCabinetIn3D} from "../../../ar3d/threeD/glbViewer/scene/modelsLoader/cabinetInterior/tallCabinetUpdate3D";
import {reloadShapesIn3DMode} from "../../../../helpers/3dHelper/modelsHelper";
import {is3DMode} from "../../NavigationHelper";
import SqftStore from '../../../canvas/store/sqftStore';
import CabinetDoorExtensionPreview from './CabinetDoorExtensionPreview';
import {CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE} from '../../../../constants/cabinetInteriorConstants';
import {
  ALIGN,
  MAX_SECTIONS,
  MAX_SECTION_QUANTITY,
  DEFAULT_SECTION_HEIGHT,
  buildLayoutFromGlbSections,
  createDefaultCabinetInteriorLayout,
  createCabinetInteriorSection,
  canSectionHaveDoor,
  normalizeLayoutForShape,
  roundHeight,
  sumSectionHeights,
} from './helpers/cabinetInteriorLayoutHelper';

const SECTION_TYPE_LABELS = {
  [SECTION_TYPE.box]: 'Box',
  [SECTION_TYPE.shelf]: 'Shelf',
  [SECTION_TYPE.drawer]: 'Drawer',
};

let doorExtensionGroupCounter = 0;
const createDoorExtensionGroupId = () => `door-extension-group-${++doorExtensionGroupCounter}`;

const getLayoutFromShape = (shape) => {
  if (!shape.shapeCabinet) {
    return null;
  }

  const cabinetHeight = shape.getHeight();

  return normalizeLayoutForShape(
    shape.shapeCabinet.getCabinetInteriorLayout(),
    getCabinetInteriorHeight(cabinetHeight)
  );
};

const SectionCabinetInterior = ({ shape, isDoorExtensionEnabled }) => {
  const cabinetHeight = shape.getHeight();
  const interiorHeight = getCabinetInteriorHeight(cabinetHeight);
  const [layout, setLayout] = useState(() => getLayoutFromShape(shape));
  const [isLoadingFromModel, setIsLoadingFromModel] = useState(layout === null);
  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);

  useEffect(() => {
    const nextLayout = getLayoutFromShape(shape);

    if (nextLayout) {
      shape.shapeCabinet.setCabinetInteriorLayout(nextLayout);
    }

    setLayout(nextLayout);
    setIsLoadingFromModel(nextLayout === null);
  }, [shape, interiorHeight]);

  useEffect(() => {
    if (layout !== null) {
      return;
    }

    let cancelled = false;
    const modelUrl = getShape3dModelUrl(shape);

    getCabinetSectionsFromModel(modelUrl).then((glbSections) => {
      if (cancelled) {
        return;
      }

      const resolved = glbSections
        ? buildLayoutFromGlbSections(glbSections, interiorHeight)
        : createDefaultCabinetInteriorLayout(interiorHeight);

      shape.shapeCabinet.setCabinetInteriorLayout(resolved);
      setLayout(resolved);
      setIsLoadingFromModel(false);
      SqftStore.updateCalculations();
    });

    return () => { cancelled = true; };
  }, [layout, shape, interiorHeight]);

  const saveLayout = useCallback((updater, shouldReloadModel = false) => {
    const nextDraft = typeof updater === 'function' ? updater(layout) : updater;
    const next = normalizeLayoutForShape(nextDraft, interiorHeight);

    if (!next) {
      return;
    }

    shape.shapeCabinet.setCabinetInteriorLayout(next);
    setLayout(next);
    SqftStore.updateCalculations();
    if (shouldReloadModel && is3DMode()) {
      // Structural edits create new authored subtrees. Reload from the clean
      // GLB so a section added after live resizing cannot inherit mutated
      // transforms or geometry from the current scene instance.
      reloadShapesIn3DMode([shape]).catch((error) => {
        console.error('[Cabinet interior] Could not reload cabinet after adding a section:', error);
      });
    } else {
      updateTallCabinetIn3D(shape.id, next);
    }
  }, [shape, layout, interiorHeight]);

  const setAlignment = (alignment) => {
    saveLayout((prev) => ({ ...prev, alignment }));
  };

  const addSection = () => {
    const remainingHeight = roundHeight(interiorHeight - sumSectionHeights(layout.sections));
    if (layout.sections.length >= MAX_SECTIONS || remainingHeight < 1) {
      return;
    }

    saveLayout((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        createCabinetInteriorSection(SECTION_TYPE.box, Math.min(DEFAULT_SECTION_HEIGHT, remainingHeight)),
      ],
    }), true);
  };

  const removeSection = (id) => {
    if (layout.sections.length <= 1) {
      return;
    }

    saveLayout((prev) => ({
      ...prev,
      sections: prev.sections.filter((s) => s.id !== id),
    }));
  };

  const moveSection = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= layout.sections.length) {
      return;
    }

    saveLayout((prev) => {
      const sections = [...prev.sections];
      [sections[index], sections[targetIndex]] = [sections[targetIndex], sections[index]];
      return { ...prev, sections };
    });
  };

  const reorderSection = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) {
      return;
    }

    saveLayout((prev) => {
      const fromIndex = prev.sections.findIndex((section) => section.id === fromId);
      const toIndex = prev.sections.findIndex((section) => section.id === toId);

      if (fromIndex < 0 || toIndex < 0) {
        return prev;
      }

      const sections = [...prev.sections];
      const [movedSection] = sections.splice(fromIndex, 1);
      sections.splice(toIndex, 0, movedSection);

      return { ...prev, sections };
    });
  };

  const handleSectionDragStart = (event, sectionId) => {
    setDraggedSectionId(sectionId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', sectionId);
  };

  const handleSectionDragOver = (event, sectionId) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverSectionId(sectionId);
  };

  const handleSectionDrop = (event, sectionId) => {
    event.preventDefault();
    const fromId = event.dataTransfer.getData('text/plain') || draggedSectionId;

    reorderSection(fromId, sectionId);
    setDraggedSectionId(null);
    setDragOverSectionId(null);
  };

  const handleSectionDragEnd = () => {
    setDraggedSectionId(null);
    setDragOverSectionId(null);
  };

  const updateSectionType = (id, type) => {
    saveLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => {
        if (s.id !== id) {
          return s;
        }

        const nextSection = { ...s, type };
        nextSection.quantity = type === SECTION_TYPE.box
          ? 0
          : Math.max(1, nextSection.quantity || 1);
        nextSection.hasDoor = canSectionHaveDoor(type)
          ? nextSection.hasDoor !== false
          : false;
        nextSection.hasDoorExtension = type === SECTION_TYPE.box
          ? nextSection.hasDoorExtension
          : false;

        return nextSection;
      }),
    }));
  };

  const setDoorExtensionGroupForRange = (sections, startIndex, endIndex, groupId) => {
    const rangeLength = endIndex - startIndex + 1;

    return sections.map((section, index) => {
      if (index < startIndex || index > endIndex) {
        return section;
      }

      if (rangeLength < 2) {
        return {
          ...section,
          hasDoorExtension: false,
          doorExtensionGroupId: null,
        };
      }

      return {
        ...section,
        hasDoor: true,
        hasDoorExtension: true,
        doorExtensionGroupId: groupId,
      };
    });
  };

  const toggleDoorExtensionBoundary = (leftId, rightId) => {
    if (!isDoorExtensionEnabled) {
      return;
    }

    saveLayout((prev) => ({
      ...prev,
      sections: (() => {
        const leftIndex = prev.sections.findIndex((section) => section.id === leftId);
        const rightIndex = prev.sections.findIndex((section) => section.id === rightId);
        if (leftIndex < 0 || rightIndex !== leftIndex + 1) {
          return prev.sections;
        }

        const leftSection = prev.sections[leftIndex];
        const rightSection = prev.sections[rightIndex];
        if (leftSection.type !== SECTION_TYPE.box || rightSection.type !== SECTION_TYPE.box) {
          return prev.sections;
        }

        const leftGroupId = leftSection.doorExtensionGroupId;
        const rightGroupId = rightSection.doorExtensionGroupId;
        if (leftGroupId && leftGroupId === rightGroupId) {
          let groupStartIndex = leftIndex;
          let groupEndIndex = rightIndex;

          while (groupStartIndex > 0 && prev.sections[groupStartIndex - 1].doorExtensionGroupId === leftGroupId) {
            groupStartIndex--;
          }
          while (
            groupEndIndex < prev.sections.length - 1
            && prev.sections[groupEndIndex + 1].doorExtensionGroupId === leftGroupId
          ) {
            groupEndIndex++;
          }

          let sections = setDoorExtensionGroupForRange(
            prev.sections,
            groupStartIndex,
            leftIndex,
            createDoorExtensionGroupId()
          );
          sections = setDoorExtensionGroupForRange(
            sections,
            rightIndex,
            groupEndIndex,
            createDoorExtensionGroupId()
          );
          return sections;
        }

        let rangeStart = leftIndex;
        let rangeEnd = rightIndex;

        while (rangeStart > 0 && leftGroupId && prev.sections[rangeStart - 1].doorExtensionGroupId === leftGroupId) {
          rangeStart--;
        }
        while (
          rangeEnd < prev.sections.length - 1
          && rightGroupId
          && prev.sections[rangeEnd + 1].doorExtensionGroupId === rightGroupId
        ) {
          rangeEnd++;
        }

        const canMergeRange = prev.sections
          .slice(rangeStart, rangeEnd + 1)
          .every((section) => section.type === SECTION_TYPE.box);

        if (!canMergeRange) {
          return prev.sections;
        }

        return setDoorExtensionGroupForRange(
          prev.sections,
          rangeStart,
          rangeEnd,
          createDoorExtensionGroupId()
        );
      })(),
    }));
  };

  const updateSectionDoor = (id, hasDoor) => {
    saveLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.id !== id || !canSectionHaveDoor(section.type)) {
          return section;
        }

        return {
          ...section,
          hasDoor,
          hasDoorExtension: hasDoor ? section.hasDoorExtension : false,
          doorExtensionGroupId: hasDoor ? section.doorExtensionGroupId : null,
        };
      }),
    }));
  };

  const updateSectionQuantity = (id, rawValue) => {
    const quantity = parseInt(rawValue, 10);
    const section = layout.sections.find((s) => s.id === id);
    if (!section || section.type === SECTION_TYPE.box || isNaN(quantity) || quantity < 1) {
      return;
    }

    saveLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (
        s.id === id
          ? { ...s, quantity: Math.min(quantity, MAX_SECTION_QUANTITY) }
          : s
      )),
    }));
  };

  const updateSectionHeight = (id, rawValue) => {
    const height = parseFloat(rawValue);
    if (isNaN(height) || height <= 0) {
      return;
    }

    const currentSection = layout.sections.find((section) => section.id === id);
    if (!currentSection) {
      return;
    }

    const occupiedHeight = sumSectionHeights(layout.sections) - currentSection.height;
    const maxHeight = Math.max(1, roundHeight(interiorHeight - occupiedHeight));
    const nextHeight = Math.min(roundHeight(height), maxHeight);

    saveLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === id ? { ...s, height: nextHeight } : s)),
    }));
  };

  const sectionLetterLabel = (index) => String.fromCharCode(65 + index);
  const totalSectionHeight = layout ? roundHeight(sumSectionHeights(layout.sections)) : 0;
  const remainingHeight = layout ? roundHeight(Math.max(0, interiorHeight - totalSectionHeight)) : 0;
  const getSectionDragStyle = (sectionId) => ({
    opacity: draggedSectionId === sectionId ? 0.55 : 1,
    borderColor: dragOverSectionId === sectionId && draggedSectionId !== sectionId ? '#4A90E2' : undefined,
  });
  if (isLoadingFromModel) {
    return (
      <div className="sr__section sr__tall-cabinet-interior">
        <CabinetDoorExtensionPreview
          layout={layout}
          interiorHeight={interiorHeight}
          isDoorExtensionEnabled={isDoorExtensionEnabled}
          onBoundaryClick={toggleDoorExtensionBoundary}
        />
        <div className="tci__loading">Loading sections from model...</div>
      </div>
    );
  }

  if (!layout) {
    return null;
  }

  return (
    <div className="sr__section sr__tall-cabinet-interior">
      <CabinetDoorExtensionPreview
        layout={layout}
        interiorHeight={interiorHeight}
        isDoorExtensionEnabled={isDoorExtensionEnabled}
        onBoundaryClick={toggleDoorExtensionBoundary}
      />

      <div className="sr__section-title">Cabinet Interior</div>

      {/* Alignment toggle */}
      <div className="tci__toggle-group">
        <button
          className={'tci__toggle-btn' + (layout.alignment === ALIGN.top ? ' tci__toggle-btn--active' : '')}
          onClick={() => setAlignment(ALIGN.top)}
        >
          Top Align
        </button>
        <button
          className={'tci__toggle-btn' + (layout.alignment === ALIGN.bottom ? ' tci__toggle-btn--active' : '')}
          onClick={() => setAlignment(ALIGN.bottom)}
        >
          Bottom Align
        </button>
      </div>

      {/* Sections list */}
      <div className="tci__sections">
        {layout.sections.map((section, index) => (
          <div
            key={section.id}
            className={'tci__section-item'
              + (draggedSectionId === section.id ? ' tci__section-item--dragging' : '')
              + (dragOverSectionId === section.id && draggedSectionId !== section.id ? ' tci__section-item--drag-over' : '')}
            onDragOver={(event) => handleSectionDragOver(event, section.id)}
            onDrop={(event) => handleSectionDrop(event, section.id)}
            style={getSectionDragStyle(section.id)}
          >
            <div className="tci__section-header">
              <span
                className="tci__drag-handle"
                draggable
                onDragStart={(event) => handleSectionDragStart(event, section.id)}
                onDragEnd={handleSectionDragEnd}
                title="Drag to reorder"
                style={{ cursor: 'grab', display: 'inline-flex', color: '#9aa0a6' }}
              >
                <DragIndicatorIcon fontSize="small" />
              </span>
              <span className="tci__section-label">
                {SECTION_TYPE_LABELS[section.type]} {sectionLetterLabel(index)}
              </span>
              <div className="tci__section-controls">
                <button
                  className="tci__icon-btn"
                  onClick={() => moveSection(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </button>
                <button
                  className="tci__icon-btn"
                  onClick={() => moveSection(index, 1)}
                  disabled={index === layout.sections.length - 1}
                  title="Move down"
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </button>
                <button
                  className="tci__icon-btn tci__icon-btn--danger"
                  onClick={() => removeSection(section.id)}
                  disabled={layout.sections.length <= 1}
                  title="Remove"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              </div>
            </div>

            {/* Type selector */}
            <div className="tci__type-group">
              {Object.values(SECTION_TYPE).map((type) => (
                <button
                  key={type}
                  className={'tci__type-btn' + (section.type === type ? ' tci__type-btn--active' : '')}
                  onClick={() => updateSectionType(section.id, type)}
                >
                  {SECTION_TYPE_LABELS[type]}
                </button>
              ))}
            </div>

            <label className={'tci__door-row' + (!canSectionHaveDoor(section.type) ? ' tci__door-row--disabled' : '')}>
              <input
                className="tci__door-checkbox"
                type="checkbox"
                checked={canSectionHaveDoor(section.type) && section.hasDoor !== false}
                disabled={!canSectionHaveDoor(section.type)}
                onChange={(e) => updateSectionDoor(section.id, e.target.checked)}
              />
              <span className="tci__door-label">Door</span>
            </label>

            {/* Height input */}
            <div className="tci__field-row tci__height-row">
              <img src={iconHeight} alt="Height" className="tci__height-icon" />
              <span className="tci__field-label">Height</span>
              <div className="tci__field-control">
                <input
                  className="ui-input tci__field-input"
                  type="number"
                  min={1}
                  max={interiorHeight}
                  step={0.5}
                  value={section.height}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => updateSectionHeight(section.id, e.target.value)}
                />
                <span className="tci__field-unit">&quot;</span>
              </div>
            </div>

            {section.type !== SECTION_TYPE.box ? (
              <div className="tci__field-row tci__quantity-row">
                <FormatListNumberedIcon className="tci__quantity-icon" fontSize="small" />
                <span className="tci__field-label">Quantity</span>
                <div className="tci__field-control">
                  <input
                    className="ui-input tci__field-input"
                    type="number"
                    min={1}
                    max={MAX_SECTION_QUANTITY}
                    value={section.quantity !== undefined ? section.quantity : 1}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => updateSectionQuantity(section.id, e.target.value)}
                  />
                  <span className="tci__field-unit tci__field-unit--placeholder">&quot;</span>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="tci__summary">
        Interior: {interiorHeight}&quot; / Sections: {totalSectionHeight}&quot; / Cabinet: {cabinetHeight}&quot;
        {remainingHeight > 0 ? ` (${remainingHeight}" free space)` : ''}
      </div>

      {/* Add section button */}
      <Button
        className="sr__button tci__add-section-button"
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={addSection}
        disabled={layout.sections.length >= MAX_SECTIONS || remainingHeight < 1}
        style={{ marginTop: 8 }}
      >
        Add Section
      </Button>
    </div>
  );
};

SectionCabinetInterior.propTypes = {
  shape: PropTypes.instanceOf(sqftShape).isRequired,
  isDoorExtensionEnabled: PropTypes.bool.isRequired,
};

export default SectionCabinetInterior;

